import { useState, useEffect, useCallback } from "react"
import styles from "./courses-page.module.css"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import type { Course } from "@/lib/supabase/types"

interface Chapter {
  id: number
  title: string
  videos: number
  duration: string
}

// 课程状态映射到中文显示
const statusMap: Record<string, string> = {
  draft: "草稿",
  published: "已发布",
  archived: "已归档",
}

const mockChapters: Chapter[] = [
  { id: 1, title: "第一章：基础概念", videos: 5, duration: "1h 20min" },
  { id: 2, title: "第二章：核心语法", videos: 8, duration: "2h 15min" },
  { id: 3, title: "第三章：高级特性", videos: 6, duration: "1h 45min" },
]

export default function CoursesPage() {
  const [activeTab, setActiveTab] = useState<"all" | "published" | "draft">("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [showChapterModal, setShowChapterModal] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)
  const [editingCourse, setEditingCourse] = useState<Partial<Course>>({})
  const [activeTab1, setActiveTab1] = useState<"chapters" | "settings">("chapters")
  const [courses, setCourses] = useState<Course[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState("")

  // 获取课程数据
  const fetchCourses = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error
      setCourses(data || [])
    } catch (error) {
      console.error("获取课程数据失败:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCourses()
  }, [fetchCourses])

  // 过滤课程
  const filteredCourses = courses.filter((course) => {
    const matchesTab =
      activeTab === "all" ||
      (course.status === "published" && activeTab === "published") ||
      (course.status === "draft" && activeTab === "draft")
    const matchesSearch =
      course.title.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !selectedCategory || course.category === selectedCategory
    return matchesTab && matchesSearch && matchesCategory
  })

  const handleCreateCourse = () => {
    setEditingCourse({})
    setShowModal(true)
  }

  const handleEditCourse = (course: Course) => {
    setEditingCourse({ ...course })
    setShowModal(true)
  }

  const handleViewChapters = (course: Course) => {
    setSelectedCourse(course)
    setShowChapterModal(true)
  }

  const handleSaveCourse = async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      const now = new Date().toISOString()
      const courseData = {
        ...editingCourse,
        updated_at: now,
      }

      if (editingCourse.id) {
        // 更新现有课程
        const { error } = await supabase
          .from("courses")
          .update(courseData)
          .eq("id", editingCourse.id)
        if (error) throw error
      } else {
        // 创建新课程
        const { error } = await supabase.from("courses").insert({
          ...courseData,
          created_at: now,
        })
        if (error) throw error
      }

      setShowModal(false)
      setEditingCourse({})
      fetchCourses()
    } catch (error) {
      console.error("保存课程失败:", error)
      alert("保存失败，请重试")
    }
  }

  const handleDeleteCourse = async (id: string) => {
    if (!confirm("确定要删除这个课程吗？此操作不可恢复。")) return

    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      const { error } = await supabase.from("courses").delete().eq("id", id)
      if (error) throw error
      fetchCourses()
    } catch (error) {
      console.error("删除课程失败:", error)
      alert("删除失败，请重试")
    }
  }

  // 统计
  const stats = {
    total: courses.length,
    published: courses.filter((c) => c.status === "published").length,
    draft: courses.filter((c) => c.status === "draft").length,
    totalStudents: courses.reduce((sum, c) => sum + (c.enrolled_count || 0), 0),
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>课程管理</h1>
        </div>
        <div className={styles.stats}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.statItem}>
              <LoadingSkeleton variant="text" />
            </div>
          ))}
        </div>
        <div className={styles.searchBar}>
          <LoadingSkeleton variant="text" />
        </div>
        <div className={styles.courseList}>
          {[1, 2, 3].map((i) => (
            <LoadingSkeleton key={i} variant="rectangular" height={150} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>课程管理</h1>
        <button className={styles.addBtn} onClick={handleCreateCourse}>
          + 创建课程
        </button>
      </div>

      {/* 统计卡片 */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.total}</span>
          <span className={styles.statLabel}>课程总数</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.published}</span>
          <span className={styles.statLabel}>已发布</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.draft}</span>
          <span className={styles.statLabel}>草稿</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.totalStudents.toLocaleString()}</span>
          <span className={styles.statLabel}>订阅人数</span>
        </div>
      </div>

      {/* 搜索栏 */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="搜索课程名称..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
        <select className={styles.filterSelect} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
          <option value="">全部类别</option>
          <option value="frontend">前端开发</option>
          <option value="backend">后端开发</option>
          <option value="mobile">移动开发</option>
          <option value="ai">人工智能</option>
        </select>
      </div>

      {/* 标签页 */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "all" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("all")}
        >
          全部课程
        </button>
        <button
          className={`${styles.tab} ${activeTab === "published" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("published")}
        >
          已发布
        </button>
        <button
          className={`${styles.tab} ${activeTab === "draft" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("draft")}
        >
          草稿
        </button>
      </div>

      {/* 课程列表 */}
      <div className={styles.courseList}>
        {filteredCourses.length === 0 ? (
          <div className={styles.emptyState}>暂无课程数据</div>
        ) : (
          filteredCourses.map((course) => (
            <div key={course.id} className={styles.courseCard}>
              <div className={styles.courseCover}>
                {course.cover_url ? (
                  <img src={course.cover_url} alt={course.title} className={styles.coverImage} />
                ) : (
                  <span className={styles.courseIcon}>📚</span>
                )}
              </div>
              <div className={styles.courseInfo}>
                <h3>{course.title}</h3>
                <p className={styles.instructor}>难度: {course.difficulty || "未设置"}</p>
                <div className={styles.courseMeta}>
                  <span>{course.enrolled_count || 0} 学生</span>
                  <span>{(course.price_bbp / 100).toLocaleString()} BBP</span>
                  <span>{course.total_chapters || 0} 章节</span>
                </div>
                <span className={`${styles.status} ${styles[course.status]}`}>
                  {statusMap[course.status] || course.status}
                </span>
              </div>
              <div className={styles.actions}>
                <button className={styles.viewBtn} onClick={() => handleViewChapters(course)}>
                  章节
                </button>
                <button className={styles.editBtn} onClick={() => handleEditCourse(course)}>
                  编辑
                </button>
                <button className={styles.deleteBtn} onClick={() => handleDeleteCourse(course.id)}>
                  删除
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 创建/编辑课程弹窗 */}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{editingCourse.id ? "编辑课程" : "创建课程"}</h2>
              <button className={styles.closeBtn} onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>课程名称</label>
                <input
                  type="text"
                  placeholder="输入课程名称"
                  value={editingCourse.title || ""}
                  onChange={(e) => setEditingCourse({ ...editingCourse, title: e.target.value })}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>课程描述</label>
                <textarea
                  placeholder="输入课程简介..."
                  value={editingCourse.description || ""}
                  onChange={(e) => setEditingCourse({ ...editingCourse, description: e.target.value })}
                  className={styles.textarea}
                  rows={3}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>价格 (BBP)</label>
                  <input
                    type="number"
                    placeholder="29900"
                    value={editingCourse.price_bbp || ""}
                    onChange={(e) =>
                      setEditingCourse({ ...editingCourse, price_bbp: Number(e.target.value) })
                    }
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>难度</label>
                  <select
                    className={styles.select}
                    value={editingCourse.difficulty || ""}
                    onChange={(e) =>
                      setEditingCourse({ ...editingCourse, difficulty: e.target.value })
                    }
                  >
                    <option value="">请选择</option>
                    <option value="beginner">初级</option>
                    <option value="intermediate">中级</option>
                    <option value="advanced">高级</option>
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>总章节数</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={editingCourse.total_chapters || ""}
                    onChange={(e) =>
                      setEditingCourse({ ...editingCourse, total_chapters: Number(e.target.value) })
                    }
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>状态</label>
                  <select
                    className={styles.select}
                    value={editingCourse.status || "draft"}
                    onChange={(e) =>
                      setEditingCourse({ ...editingCourse, status: e.target.value as Course["status"] })
                    }
                  >
                    <option value="draft">草稿</option>
                    <option value="published">已发布</option>
                    <option value="archived">已归档</option>
                  </select>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>课程封面 URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/cover.jpg"
                  value={editingCourse.cover_url || ""}
                  onChange={(e) => setEditingCourse({ ...editingCourse, cover_url: e.target.value })}
                  className={styles.input}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>
                取消
              </button>
              <button className={styles.submitBtn} onClick={handleSaveCourse}>
                {editingCourse.id ? "保存" : "创建"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 章节管理弹窗 */}
      {showChapterModal && selectedCourse && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalLarge}>
            <div className={styles.modalHeader}>
              <h2>{selectedCourse.title} - 章节管理</h2>
              <button className={styles.closeBtn} onClick={() => setShowChapterModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.chapterTabs}>
              <button
                className={`${styles.chapterTab} ${activeTab1 === "chapters" ? styles.activeChapterTab : ""}`}
                onClick={() => setActiveTab1("chapters")}
              >
                章节列表
              </button>
              <button
                className={`${styles.chapterTab} ${activeTab1 === "settings" ? styles.activeChapterTab : ""}`}
                onClick={() => setActiveTab1("settings")}
              >
                章节设置
              </button>
            </div>
            <div className={styles.modalBody}>
              {activeTab1 === "chapters" ? (
                <div className={styles.chapterList}>
                  {mockChapters.map((chapter, index) => (
                    <div key={chapter.id} className={styles.chapterItem}>
                      <div className={styles.chapterInfo}>
                        <span className={styles.chapterIndex}>{index + 1}</span>
                        <div className={styles.chapterText}>
                          <h4>{chapter.title}</h4>
                          <span className={styles.chapterMeta}>
                            {chapter.videos} 个视频 · {chapter.duration}
                          </span>
                        </div>
                      </div>
                      <div className={styles.chapterActions}>
                        <button className={styles.iconBtn} title="编辑">
                          ✏️
                        </button>
                        <button className={styles.iconBtn} title="删除">
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                  <button className={styles.addChapterBtn}>+ 添加章节</button>
                </div>
              ) : (
                <div className={styles.chapterSettings}>
                  <div className={styles.settingRow}>
                    <label>允许试看</label>
                    <input type="checkbox" className={styles.checkbox} defaultChecked />
                  </div>
                  <div className={styles.settingRow}>
                    <label>免费试看章节</label>
                    <select className={styles.select}>
                      <option value="">选择章节</option>
                      <option value="1">第一章：基础概念</option>
                      <option value="2">第二章：核心语法</option>
                    </select>
                  </div>
                  <div className={styles.settingRow}>
                    <label>完成后颁发证书</label>
                    <input type="checkbox" className={styles.checkbox} defaultChecked />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
